import { inject, Injectable } from '@angular/core';
import { SmartToastService } from '@shared/services/smart-toast.service';
import { FormGroup, Validators } from '@angular/forms';
import { CoopValidators } from '@core/helpers/validators.helper';
import { DatiBilancioFieldValidators } from './validators/dati-bilancio.field-validators';
import { DatiBilancioWarningsService } from './warnings/dati-bilancio.warnings';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { DatiBilancioFormListeners } from './listeners/dati-bilancio.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { datiBilancioFormValidators } from './validators/dati-bilancio.form-validators';
import { formPaths } from '@shared/constants/form-paths.const';
import { getDatiBilancioGroup } from './validators/helpers/dati-bilancio-form.helpers';
import {
    buildCertificazioniPassateFormArray,
    buildContrattoSocCertificazioneFormArray,
    buildCopiaBilancioAllegatoFormArray,
    buildEntiErogatoreFormArray,
    buildNotaIntegrativaAllegataFormArray,
    buildRelazCertifAllegataFormArray,
    buildSocietaPartecipateFormArray,
} from './common/builders/dati-bilancio-form-array.builder';

@Injectable()
export class DatiBilancioFormService extends BaseRevisioneFormService<DatiBilancioWarningsService, DatiBilancioFieldValidators> {
    protected smartToast = inject(SmartToastService);

    constructor(protected validators: DatiBilancioFieldValidators, protected warningsService: DatiBilancioWarningsService) {
        super(warningsService, validators);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Certificazioni passate
            'my_CertificazioniPassate.my_ElCertificazioniPassate',

            // Flag prevalenza
            'my_InseritoB9VersoSoci',
            'my_InseritoA1VersoSoci',
            'my_InseritoB7VersoSoci',
            'my_InseritoB6QtaVersoSoci',
            'my_InseritoB6VersoSoci',
            'my_rilevaCodFiscSocPartecipate',
            'my_PartecPiuDi50InSpa',

            // Warning ristorni
            'my_WarningsRistorni.my_CkDSuperioreB',
            'my_WarningsRistorni.my_CkDSuperioreBAnno1',
            'my_WarningsRistorni.my_CkDSuperiore30PerCentoC',
            'my_WarningsRistorni.my_CkDSuperiore30PerCentoCAnno1',
            'my_WarningsRistorni.my_CkDSuperioreCEB9',
            'my_WarningsRistorni.my_CkDSuperioreCEB9Anno1',
            'my_WarningsRistorni.my_CkESuperioreB',
            'my_WarningsRistorni.my_CkESuperioreBAnno1',
            'my_WarningsRistorni.my_CkFSuperioreB',
            'my_WarningsRistorni.my_CkFSuperioreBAnno1',
            'my_WarningsRistorni.my_CkGSuperioreB',
            'my_WarningsRistorni.my_CkGSuperioreBAnno1',
            'my_WarningsRistorni.my_CkHSuperioreB',
            'my_WarningsRistorni.my_CkHSuperioreBAnno1',
            'my_WarningsRistorni.my_CkISuperioreB',
            'my_WarningsRistorni.my_CkISuperioreBAnno1',
            'my_WarningsRistorni.my_CkGHISuperioriB',
            'my_WarningsRistorni.my_CkGHISuperioriBAnno1',
            'my_WarningsRistorni.my_MsgCkDSuperioreB',
            'my_WarningsRistorni.my_MsgCkDSuperiore30PerCentoC',
            'my_WarningsRistorni.my_MsgCkDSuperioreCEB9',
            'my_WarningsRistorni.my_MsgCkESuperioreB',
            'my_WarningsRistorni.my_MsgCkFSuperioreB',
            'my_WarningsRistorni.my_MsgCkGSuperioreB',
            'my_WarningsRistorni.my_MsgCkHSuperioreB',
            'my_WarningsRistorni.my_MsgCkISuperioreB',
            'my_WarningsRistorni.my_MsgCkGHISuperioriB',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,
            `${formPaths.datiIdentificativi}.my_CkImpresaSociale`,

            // Natura mutualistica
            `${formPaths.naturaMutualistica}.my_CkOffreOccLavSoci`,
            `${formPaths.naturaMutualistica}.my_CkOffreBeniServ`,
            `${formPaths.naturaMutualistica}.my_CkRemunPrestConfServSoci`,
            `${formPaths.naturaMutualistica}.my_CkRemunConferProdSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_lblScheda`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_A1versoSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_B9versoSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_B6confSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_B7ricevSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_QtaConfSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_A1versoSociPrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_B9versoSociPrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_B6confSociPrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_B7ricevSociPrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleA1`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleA1PrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleB9`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleB9PrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleB6`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleB6PrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleB7`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotaleB7PrimoAnno`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_CkValoreQuantita`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_CkValoreQuantitaPrimoAnno`,

            // Scheda coop sociali
            `${formPaths.schedaCoopSociali}.my_ScSocObbl`,

            // Effettività base sociale
            `${formPaths.baseSociale}.my_SociFisCoopData`,
            `${formPaths.baseSociale}.my_SociFisFinData`,
            `${formPaths.baseSociale}.my_gr0001.my_SociSpecData_personeFisiche`,
            `${formPaths.baseSociale}.my_SociGiuFinData`,
            `${formPaths.baseSociale}.my_SociGiuCoopData`,
            `${formPaths.baseSociale}.my_SociSpecData`,
            `${formPaths.baseSociale}.my_ChiusuraEsercizio`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_TipoBilancioAbbr2`,
            `${formPaths.datiBilancio}.my_TipoBilancioAbbr1`,
            `${formPaths.datiBilancio}.my_DataChiusura2`,
            `${formPaths.datiBilancio}.my_DataChiusura1`,
            `${formPaths.datiBilancio}.my_DataApprovazione2`,
            `${formPaths.datiBilancio}.my_DataApprovazione1`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil2`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcB9tot2`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcB72`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcB9tot1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcB71`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAI1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAI2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtAtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtAtot1`,
        ]);
    }

    override initListener(): void {
        new DatiBilancioFormListeners(this._form, this.store, this.smartToast).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_CertificazioniPassate: this.fb.group({
                    my_ElCertificazioniPassate: this.fb.array([]),
                }),

                my_WarningsRistorni: this.fb.group({
                    my_CkDSuperioreB: '',
                    my_CkDSuperioreBAnno1: '',
                    my_CkDSuperiore30PerCentoC: '',
                    my_CkDSuperiore30PerCentoCAnno1: '',
                    my_CkDSuperioreCEB9: '',
                    my_CkDSuperioreCEB9Anno1: '',
                    my_CkESuperioreB: '',
                    my_CkESuperioreBAnno1: '',
                    my_CkFSuperioreB: '',
                    my_CkFSuperioreBAnno1: '',
                    my_CkGSuperioreB: '',
                    my_CkGSuperioreBAnno1: '',
                    my_CkHSuperioreB: '',
                    my_CkHSuperioreBAnno1: '',
                    my_CkISuperioreB: '',
                    my_CkISuperioreBAnno1: '',
                    my_CkGHISuperioriB: '',
                    my_CkGHISuperioriBAnno1: '',
                    my_MsgCkDSuperioreB: '',
                    my_MsgCkDSuperiore30PerCentoC: '',
                    my_MsgCkDSuperioreCEB9: '',
                    my_MsgCkESuperioreB: '',
                    my_MsgCkFSuperioreB: '',
                    my_MsgCkGSuperioreB: '',
                    my_MsgCkHSuperioreB: '',
                    my_MsgCkISuperioreB: '',
                    my_MsgCkGHISuperioriB: '',
                }),

                my_InseritoB9VersoSoci: '',
                my_InseritoA1VersoSoci: '',
                my_InseritoB7VersoSoci: '',
                my_InseritoB6QtaVersoSoci: '',
                my_InseritoB6VersoSoci: '',
                my_rilevaCodFiscSocPartecipate: 'S',
                my_PartecPiuDi50InSpa: '',

                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                        my_CkImpresaSociale: '',
                    }),

                    my_NaturaMutualistica: this.fb.group({
                        my_CkOffreOccLavSoci: '',
                        my_CkOffreBeniServ: '',
                        my_CkRemunPrestConfServSoci: '',
                        my_CkRemunConferProdSoci: '',
                        my_SchedaPrevalenza: this.fb.group({
                            my_lblScheda: '',
                            my_A1versoSoci: '',
                            my_B9versoSoci: '',
                            my_B6confSoci: '',
                            my_B7ricevSoci: '',
                            my_QtaConfSoci: '',
                            my_A1versoSociPrimoAnno: '',
                            my_B9versoSociPrimoAnno: '',
                            my_B6confSociPrimoAnno: '',
                            my_B7ricevSociPrimoAnno: '',
                            my_TotaleA1: '',
                            my_TotaleA1PrimoAnno: '',
                            my_TotaleB9: '',
                            my_TotaleB9PrimoAnno: '',
                            my_TotaleB6: '',
                            my_TotaleB6PrimoAnno: '',
                            my_TotaleB7: '',
                            my_TotaleB7PrimoAnno: '',
                            my_CkValoreQuantita: '',
                            my_CkValoreQuantitaPrimoAnno: '',
                        }),
                    }),

                    my_SchedaCoopSociali: this.fb.group({
                        my_ScSocObbl: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_SociFisCoopData: '',
                        my_SociFisFinData: '',
                        my_gr0001: this.fb.group({
                            my_SociSpecData_personeFisiche: '',
                        }),
                        my_SociGiuFinData: '',
                        my_SociGiuCoopData: '',
                        my_SociSpecData: '',
                        my_ChiusuraEsercizio: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_RagSocRevIncaricataChoice: '',
                        my_RagSocRevIncaricataDesc: '',
                        my_RagSocRevIncaricata: '',
                        my_RagSocRevIncaricataConvenzionata: '',
                        my_CkIndiceIndebit: '',
                        my_EnableCkEnteTenutoCertBil: '',
                        my_NoteAlBilancio: '',
                        my_RelazCertifAllegata: this.fb.group({
                            my_ElRelazCertifAllegata: this.fb.array([]),
                        }),
                        my_ContrattoSocietaCertificazione: this.fb.group({
                            my_ElContrattoSocietaCertificazione: this.fb.array([]),
                        }),
                        my_CopiaBilancioAllegato: this.fb.group({
                            my_ElCopiaBilancioAllegato: this.fb.array([]),
                        }),
                        my_BilancioAllegato: '',
                        my_NotaIntegrativaAllegata: this.fb.group({
                            my_ElNotaIntegrativaAllegata: this.fb.array([]),
                        }),
                        my_DataChiusura2: '',
                        my_CkConformSchemaBil: ['', CoopValidators.required],
                        my_CkRispTermAppBil: ['', CoopValidators.required],
                        my_CkBilRegDeposURI: ['', CoopValidators.required],
                        my_CkSociSovventoriTraSociCoop: ['', CoopValidators.required],
                        my_TotConfSociFin: '',
                        my_CkEnteTenutoCertBil: '',
                        my_CkCtrlCertifPartecSpA: '',
                        my_CkCtrlCertifPrestitiEConferim: '',
                        my_CkCtrlCertifRiserveIndivis: '',
                        my_CkCtrlCertifValoreProd: '',
                        my_DifformitaRispAutoCertif: '',
                        my_CkProvvedeCertBil: '',
                        my_EsitoCertificazioneBilancio: '',
                        my_AnnoPrimoEs: '',
                        my_AnnoSecondoEs: '',
                        my_ImportoPatrPrimoEs: '',
                        my_ImportoPatrSecondoEs: '',
                        my_AnnoPCapSoc: ['', CoopValidators.required],
                        my_AnnoSCapSoc: ['', CoopValidators.required],
                        my_CapSocSottoscrPrimoEs: ['', CoopValidators.required],
                        my_CapSocSottoscrSecondoEs: ['', CoopValidators.required],
                        my_CapSocVersatoPrimoEs: ['', CoopValidators.required],
                        my_CapSocVersatoSecondoEs: ['', CoopValidators.required],
                        my_CkSuddivCapitSoc: ['', CoopValidators.required],
                        my_ValoreNomSuddivCapit: '',
                        my_CkApplicatiRistorni: ['', CoopValidators.required],
                        my_CkApplicaRistorniAnno1: ['', CoopValidators.required],
                        my_CkPrestitoSoci: ['', CoopValidators.required],
                        my_CkPrestitoSociCorretto: '',
                        my_CkContribPubb: ['', CoopValidators.required],
                        my_DescContribPubb: '',
                        my_NoteEdOsserv: ['', Validators.required],
                        my_EntiErogatori: this.fb.group({
                            my_ElEnteErogatore: this.fb.array([]),
                        }),
                        my_CkObbligoBilSoc: ['', CoopValidators.required],
                        my_CkRedazBilSociale: ['', CoopValidators.required],
                        my_NoteBilSociale: '',
                        my_CkRegolRedBilConsol: '',
                        my_CkRegolDepBilConsol: '',
                        my_CkRedazBilConsol: ['', CoopValidators.required],
                        my_SocietaPartecipate: this.fb.group({
                            my_ElSocietaPartecipate: this.fb.array([]),
                        }),
                        my_lblContrPubb: '',
                        my_CtrlAllegatoCertificazione: '',
                        my_CkRistorni: '',
                        my_NumBilanciInExcel: '',
                        my_CtrlAllegatoContrattoCertificazione: '',
                        my_CkEnteControllaSocObbRevCont: ['', CoopValidators.required],
                        my_CK37DichFiscPresDaUltRev: ['', CoopValidators.required],
                        my_DescDichFiscPresDaUltRev: '',
                        my_SchedaRistorni: this.fb.group({
                            my_Rigo23ContoEc: '',
                            my_Rigo23ContoEcAnno1: '',
                            my_RistorniContoEc: '',
                            my_RistorniContoEcAnno1: '',
                            my_EventualeDContoEc: '',
                            my_EventualeDContoEcAnno1: '',
                            my_EventualeEContoEc: '',
                            my_EventualeEContoEcAnno1: '',
                            my_AvanzoDiGestioneRigo23: '',
                            my_AvanzoDiGestioneRigo23Anno1: '',
                            my_AvanzoDiGestionePrevalenza: '',
                            my_AvanzoDiGestionePrevalenzaAnno1: '',
                            my_CostoPersCoopLav: '',
                            my_CostoPersCoopLavAnno1: '',
                            my_RistornoCoopLav: '',
                            my_RistornoCoopLavAnno1: '',
                            my_RistornoCoopUt: '',
                            my_RistornoCoopUtAnno1: '',
                            my_RistornoCoopAp: '',
                            my_RistornoCoopApAnno1: '',
                            my_DataDelibIntegSal: '',
                            my_OrganoLiquidazioneSalariale: '',
                            my_ImportoLiquidazioniSalariali: '',
                            my_DataDelibAumCapit: '',
                            my_OrganoAumentoGratuito: '',
                            my_ImportoAumentoGratuito: '',
                            my_DataDelibDistribTit: '',
                            my_OrganoDistribuzioneGratuita: '',
                            my_ImportoDistribuzioneGratuita: '',
                            my_DataDelibIntegSalAnno1: '',
                            my_OrganoLiquidazioneSalarialeAnno1: '',
                            my_ImportoLiquidazioniSalarialiAnno1: '',
                            my_DataDelibAumCapitAnno1: '',
                            my_OrganoAumentoGratuitoAnno1: '',
                            my_ImportoAumentoGratuitoAnno1: '',
                            my_DataDelibDistribTitAnno1: '',
                            my_OrganoDistribuzioneGratuitaAnno1: '',
                            my_ImportoDistribuzioneGratuitaAnno1: '',
                            validaVerificaDelibereAnno1: '',
                            validaVerificaDelibere: '',
                            my_valida30PercentoRistorno: '',
                            my_valida30PercentoRistornoAnno1: '',
                            my_validascheda2: '',
                            my_validascheda2Anno1: '',
                            my_lblVerifCont: '',
                            my_lblVerificaDelibere: '',
                        }),
                        my_SchedaVerificaPrestito: this.fb.group({
                            my_PatrimonioBil: '',
                            my_PatrNettoUltBil: '',
                            my_PrestitoSociale: '',
                            my_CICR: '',
                            my_NumSociDataDich: '',
                            my_DataChiusEser: '',
                            my_NSociPrestitoPF: '',
                            my_PrestSociFruttPF: '',
                            my_PrestSociInfruttPF: '',
                            my_NSociPrestitoPG: '',
                            my_PrestSociFruttPG: '',
                            my_ValoreImmBil: '',
                            my_ValoreImmICI: '',

                            my_PrestSociInfruttPG: '',
                            my_CkRispettatoLimiteIndivid: '',
                            my_CkRipettatoCICR: '',
                            my_lblSociPrestito: '',
                            my_lblImportoPrestito: '',
                        }),
                        my_CkTotAttivoAnnoXBRL: '',
                        my_CkTotAttivoAnno1XBRL: '',
                        my_CkPatrNettoAnnoXBRL: '',
                        my_CkPatrNettoAnno1XBRL: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno2: '',
                        my_BilAnno1: '',
                        my_TipoBilancioAbbr2: '',
                        my_TipoBilancioAbbr1: '',
                        my_DataChiusura2: '',
                        my_DataChiusura1: '',
                        my_DataApprovazione2: '',
                        my_DataApprovazione1: '',
                        my_TipoSchemaBil2: '',
                        my_TipoSchemaBil1: '',

                        my_ContoEconomico: this.fb.group({
                            my_CoEcB9tot2: '',
                            my_CoEcB72: '',
                            my_CoEcB9tot1: '',
                            my_CoEcB71: '',
                        }),

                        my_StatoPatrimoniale__Passivo: this.fb.group({
                            my_StPaPsAI1: '',
                            my_StPaPsAI2: '',
                            my_StPaPsAtot1: '',
                            my_StPaPsAtot2: '',
                        }),

                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtAtot2: '',
                            my_StPaAtAtot1: '',
                        }),
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: datiBilancioFormValidators,
            }
        );
    }

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        this._patchCheckBox(myVerbale);
        buildCertificazioniPassateFormArray(this.fb, myVerbale, this._form);
        buildEntiErogatoreFormArray(this.fb, myVerbale, this._form);
        buildSocietaPartecipateFormArray(this.fb, myVerbale, this._form);
        buildRelazCertifAllegataFormArray(this.fb, myVerbale, this._form);
        buildContrattoSocCertificazioneFormArray(this.fb, myVerbale, this._form);
        buildNotaIntegrativaAllegataFormArray(this.fb, myVerbale, this._form);
        buildCopiaBilancioAllegatoFormArray(this.fb, myVerbale, this._form);
    }

    private _patchCheckBox(myVerbale: MyVerbale): void {
        const my_CkCtrlCertifPartecSpA = myVerbale?.my_SezioneI__Rilevazione?.my_Bilancio?.my_CkCtrlCertifPartecSpA;
        const my_CkCtrlCertifPrestitiEConferim = myVerbale?.my_SezioneI__Rilevazione?.my_Bilancio?.my_CkCtrlCertifPrestitiEConferim;
        const my_CkCtrlCertifRiserveIndivis = myVerbale?.my_SezioneI__Rilevazione?.my_Bilancio?.my_CkCtrlCertifRiserveIndivis;
        const my_CkCtrlCertifValoreProd = myVerbale?.my_SezioneI__Rilevazione?.my_Bilancio?.my_CkCtrlCertifValoreProd;
        const bilancioFormGroup = getDatiBilancioGroup(this._form);
        bilancioFormGroup.patchValue(
            {
                my_CkCtrlCertifPartecSpA,
                my_CkCtrlCertifPrestitiEConferim,
                my_CkCtrlCertifRiserveIndivis,
                my_CkCtrlCertifValoreProd,
            },
            { emitEvent: false }
        );
    }
}
